// Print Configuration Persistence & Presets Management
// Sprint 1: Foundation - Persistence Module

import type { PrintConfig, PrintPreset } from '@/app/types/print';
import { STORAGE_KEYS, BUILT_IN_PRESETS, createDefaultPrintConfig } from './printConstants';

// ===== Schema Version Migration =====

/**
 * Migrate old print config to current version
 */
function migratePrintConfig(config: any): PrintConfig {
  // Currently only version 1, but future migrations would go here
  if (!config.version || config.version < 1) {
    console.warn('Migrating print config from unknown version to v1');
    // Add default values for missing fields
    return {
      ...createDefaultPrintConfig(),
      ...config,
      version: 1,
    };
  }
  
  return config;
}

// ===== Print Config Persistence =====

/**
 * Save print configuration to localStorage
 */
export function savePrintConfig(config: PrintConfig): void {
  try {
    const serialized = JSON.stringify(config);
    localStorage.setItem(STORAGE_KEYS.PRINT_CONFIG, serialized);
  } catch (error) {
    console.error('Failed to save print config:', error);
    throw new Error('Failed to save print configuration. Storage quota may be exceeded.');
  }
}

/**
 * Load print configuration from localStorage
 */
export function loadPrintConfig(): PrintConfig | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PRINT_CONFIG);
    
    if (!stored) {
      return null;
    }
    
    const parsed = JSON.parse(stored);
    const migrated = migratePrintConfig(parsed);
    
    // Convert date strings back to Date objects
    if (migrated.metadata) {
      migrated.metadata.dateCreated = new Date(migrated.metadata.dateCreated);
      migrated.metadata.dateModified = new Date(migrated.metadata.dateModified);
      migrated.metadata.datePrinted = new Date(migrated.metadata.datePrinted);
    }
    
    return migrated;
  } catch (error) {
    console.error('Failed to load print config:', error);
    return null;
  }
}

/**
 * Clear print configuration from localStorage
 */
export function clearPrintConfig(): void {
  localStorage.removeItem(STORAGE_KEYS.PRINT_CONFIG);
}

// ===== Print Presets Persistence =====

/**
 * Load all presets (built-in + custom)
 */
export function loadAllPresets(): PrintPreset[] {
  const customPresets = loadCustomPresets();
  return [...BUILT_IN_PRESETS, ...customPresets];
}

/**
 * Load custom presets from localStorage
 */
export function loadCustomPresets(): PrintPreset[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.PRINT_PRESETS);
    
    if (!stored) {
      return [];
    }
    
    const parsed = JSON.parse(stored);
    
    // Convert date strings back to Date objects
    return parsed.map((preset: any) => ({
      ...preset,
      createdAt: new Date(preset.createdAt),
      modifiedAt: new Date(preset.modifiedAt),
    }));
  } catch (error) {
    console.error('Failed to load custom presets:', error);
    return [];
  }
}

/**
 * Save custom presets to localStorage
 */
export function saveCustomPresets(presets: PrintPreset[]): void {
  try {
    // Filter out built-in presets
    const customOnly = presets.filter(p => !p.isBuiltIn);
    const serialized = JSON.stringify(customOnly);
    localStorage.setItem(STORAGE_KEYS.PRINT_PRESETS, serialized);
  } catch (error) {
    console.error('Failed to save custom presets:', error);
    throw new Error('Failed to save print presets. Storage quota may be exceeded.');
  }
}

/**
 * Add a new custom preset
 */
export function addCustomPreset(preset: PrintPreset): void {
  const existing = loadCustomPresets();
  
  // Check for duplicate ID
  if (existing.some(p => p.id === preset.id)) {
    throw new Error(`Preset with ID "${preset.id}" already exists`);
  }
  
  existing.push(preset);
  saveCustomPresets(existing);
}

/**
 * Update an existing custom preset
 */
export function updateCustomPreset(presetId: string, updates: Partial<PrintPreset>): void {
  const existing = loadCustomPresets();
  const index = existing.findIndex(p => p.id === presetId);
  
  if (index === -1) {
    throw new Error(`Preset with ID "${presetId}" not found`);
  }
  
  existing[index] = {
    ...existing[index],
    ...updates,
    modifiedAt: new Date(),
  };
  
  saveCustomPresets(existing);
}

/**
 * Delete a custom preset
 */
export function deleteCustomPreset(presetId: string): void {
  const existing = loadCustomPresets();
  const filtered = existing.filter(p => p.id !== presetId);
  
  if (filtered.length === existing.length) {
    throw new Error(`Preset with ID "${presetId}" not found`);
  }
  
  saveCustomPresets(filtered);
}

/**
 * Get a specific preset by ID
 */
export function getPresetById(presetId: string): PrintPreset | null {
  const allPresets = loadAllPresets();
  return allPresets.find(p => p.id === presetId) || null;
}

/**
 * Apply a preset to a print config
 */
export function applyPresetToConfig(config: PrintConfig, preset: PrintPreset): PrintConfig {
  return {
    ...config,
    ...preset.config,
    presetName: preset.name,
    isCustomPreset: !preset.isBuiltIn,
    // Preserve current metadata and preview settings
    metadata: config.metadata,
    preview: config.preview,
  };
}

/**
 * Create a new preset from current config
 */
export function createPresetFromConfig(
  config: PrintConfig,
  name: string,
  description?: string
): PrintPreset {
  const id = `preset-custom-${Date.now()}`;
  
  return {
    id,
    name,
    description,
    isBuiltIn: false,
    createdAt: new Date(),
    modifiedAt: new Date(),
    config: {
      area: config.area,
      format: config.format,
      // Don't include metadata/preview in preset
    },
  };
}

// ===== Validation =====

/**
 * Validate print config before saving
 */
export function validateBeforeSave(config: PrintConfig): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  // Basic structure validation
  if (!config.version) {
    errors.push('Config version is required');
  }
  
  if (!config.area) {
    errors.push('Print area is required');
  }
  
  if (!config.format) {
    errors.push('Print format is required');
  }
  
  if (!config.metadata) {
    errors.push('Metadata is required');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Validate preset before saving
 */
export function validatePreset(preset: PrintPreset): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  
  if (!preset.id) {
    errors.push('Preset ID is required');
  }
  
  if (!preset.name || preset.name.trim().length === 0) {
    errors.push('Preset name is required');
  }
  
  if (!preset.config) {
    errors.push('Preset config is required');
  }
  
  return {
    valid: errors.length === 0,
    errors,
  };
}
